import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError, type PostValue } from '../../api/client'
import {
  GENERAL_KEY,
  companyTableQuery,
  groupTableQuery,
  groupsQuery,
  isProductiviteeSettingsTab,
  post,
  productiviteeGeneralQuery,
  productiviteeSettingsShellQuery,
  saveGroupInactivity,
  saveProductiviteeGeneral,
  type ProductiviteeGeneral,
  type ProductiviteeGeneralWrite,
  type ProductiviteeGroup,
  type ProductiviteeSettingsTab,
  type TaggingRequest,
} from '../../api/settingsProductivitee'
import { escapeHtml } from '../../inventoree/format'
import { DataTable, useTableState, type TableColumn, type TableState } from '../../ui/DataTable'
import { LoadFailed } from '../../ui/LoadFailed'
import { SettingsCheck, SettingsPicker, SettingsPickerHeader, SettingsPickerItem, SettingsRadio } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'

/**
 * Settings → Productivitee: `productivitee_settings/tabList.php` +
 * Application tagging (`applications_tagging/index.php`: grupurile ca file in
 * stanga, `groupsTabs.php`, tabelul cu `Productivity::getTagForm`) + Master
 * tagging (`master_tagging/index.php`, `getCompanyTagForm`), element cu element.
 */
export function ProductiviteeSettingsPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(productiviteeSettingsShellQuery())

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isProductiviteeSettingsTab(tab)) return
    const first = shell.data.tabs[0]
    if (first) void navigate({ to: '/settings/productivitee/$tab', params: { tab: first.id } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />
  const current = isProductiviteeSettingsTab(tab) ? tab : (shell.data.tabs[0]?.id ?? '')

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          <li className="menu-header">Productivitee</li>
          {shell.data.tabs.map((item) => (
            <SidebarTab key={item.id} tab={item} current={current} />
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </aside>
      <div className="page-content-wrapper">
        {current === 'application_tagging' && <ApplicationTagging />}
        {current === 'master_tagging' && <MasterTagging />}
        {current === 'general' && <GeneralSettings />}
      </div>
    </>
  )
}

function SidebarTab({ tab, current }: { tab: ProductiviteeSettingsTab; current: string }) {
  const navigate = useNavigate()
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    void navigate({ to: '/settings/productivitee/$tab', params: { tab: tab.id } })
  }
  return (
    <li className={tab.id === current ? 'menu-tab active' : 'menu-tab'}>
      <a href={'/app/settings/productivitee/' + tab.id} title={tab.label} onClick={onClick}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

async function send(path: string, fields: Record<string, PostValue>, onDone?: () => void): Promise<boolean> {
  try {
    const body = await post(path, fields)
    toast(body.message ?? 'Application tag was changed.', 'Success', 'success')
    onDone?.()
    return true
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    return false
  }
}

const DASH = '<i class="fas fa-minus font-10"></i>'
const TAGS: [number, string][] = [[1, 'Productive app'], [2, 'Unproductive app'], [0, 'Untagged app']]

/**
 * `Productivity::getTagForm` / `getCompanyTagForm`: cele trei butoane radio
 * (productive / unproductive / untagged), in desenul `SettingsRadio`
 * (`label.od-inv-check`). Celula DataTable e text, deci piesa se scrie ca
 * markupul ei; alegerea se prinde delegat, pe `input.appTagging`.
 */
export function tagFormHtml(name: string, tag: number, data: Record<string, number>): string {
  const attrs = Object.entries(data).map(([k, v]) => ` data-${k}="${v}"`).join('')
  return TAGS.map(([value, title]) =>
    `<label class="od-inv-check" title="${title}" style="margin-right:10px"><input type="radio" class="appTagging" name="${escapeHtml(name)}" value="${value}"${attrs} aria-label="${title}"${tag === value ? ' checked' : ''}/></label>`,
  ).join('')
}

/** Aplicatia fara nume sau fara tip, ca in `groupsModel` / `appsModel`. */
function nameCell(r: Record<string, unknown>): string {
  return r.application_name ? escapeHtml(r.application_name) : DASH
}

function typeCell(r: Record<string, unknown>): string {
  return r.application_type ? escapeHtml(r.application_type) : '<span class="label label-default">No type</span>'
}

/**
 * Cele trei cuvinte din capul coloanei, pe RANDUL LOR, sub numele coloanei.
 *
 * In portal legenda statea lipita de nume, pe acelasi rand, si cele doua file
 * ieseau altfel: pe Application tagging numele e mai lung ("Change tag"), deci
 * legenda se rupea singura pe al doilea rand si capul tabelului crestea la
 * 57px; pe Master tagging ("Tag") incapea pe un rand si capul ramanea la 42px,
 * iar primul rand al tabelului pornea cu 16px mai sus decat pe fila de alaturi.
 * Asezata dinadins pe rand propriu, ruperea nu mai depinde de latimea coloanei
 * sau de lungimea numelui: amandoua capetele au aceeasi inaltime.
 */
const TAG_LEGEND = '<span style="display:block">(<span class="color-blue">productive</span>/<span class="color-warning">unproductive</span>/<span>untagged</span>)</span>'
const TAG_TITLE = 'Tag' + TAG_LEGEND

/** `groupsModel` din public/js/settings/productivitee.js. */
export function groupColumns(): TableColumn[] {
  return [
    { index: 0, title: 'Application', orderable: true, visible: true, className: 'td-small a-left', html: nameCell },
    { index: 1, title: 'Group', orderable: true, visible: true, width: 150, className: 'td-small a-left', html: (r) => (r.group_name ? escapeHtml(r.group_name) : '<span class="label label-default">No group</span>') },
    { index: 2, title: 'Type', orderable: true, visible: true, className: 'td-small a-left', html: typeCell },
    { index: 3, title: 'Change tag', titleHtml: 'Change tag' + TAG_LEGEND, orderable: false, visible: true, width: 250, className: 'td-small a-left', html: (r) => tagFormHtml(`tagging-${Number(r.GroupID)}-${Number(r.ID)}`, Number(r.tag_type), { app: Number(r.ID), group: Number(r.GroupID) }) },
  ]
}

/** `appsModel` din public/js/settings/productivitee.js. */
export function companyColumns(): TableColumn[] {
  return [
    { index: 0, title: 'Application', orderable: true, visible: true, className: 'td-small a-left', html: nameCell },
    { index: 1, title: 'Type', orderable: true, visible: true, className: 'td-small a-left', html: typeCell },
    { index: 2, title: 'Tag', titleHtml: TAG_TITLE, orderable: false, visible: true, width: 250, className: 'td-small a-left', html: (r) => tagFormHtml(`master-${Number(r.ID)}`, Number(r.tag_type), { app: Number(r.ID) }) },
  ]
}

function request(state: TableState): TaggingRequest {
  return { start: state.start, length: state.length, orderColumn: state.order.column, orderDir: state.order.dir, search: state.search }
}

/**
 * Alegerea unui radio din tabel: scrierea pleaca cu datele din el (nu din
 * starea ecranului). Daca pica, grupul se intoarce la ce scria in markup
 * (`defaultChecked` = atributul `checked` pus de server).
 *
 * Pe `click`, nu pe `change`: radio-ul e HTML pus in celula, fara nod React,
 * iar `onChange` al lui React nu vede schimbarea unui astfel de input. Clicul
 * pe eticheta deja aleasa (`defaultChecked`) nu scrie nimic.
 */
function onTagClick(write: (input: HTMLInputElement) => Promise<boolean>) {
  return (event: MouseEvent<HTMLDivElement>) => {
    const input = event.target as HTMLElement
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('appTagging') || input.defaultChecked) return
    void write(input).then((ok) => {
      if (ok) return
      const scope = input.closest('td') ?? input.parentElement
      scope?.querySelectorAll<HTMLInputElement>('input.appTagging').forEach((r) => { r.checked = r.defaultChecked })
    })
  }
}

function Forbidden() {
  return (
    <div className="alert alert-danger">
      <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
    </div>
  )
}

/* ------------------------------------------------------ pe grup */

function ApplicationTagging() {
  const client = useQueryClient()
  const groups = useQuery(groupsQuery())
  const [group, setGroup] = useState<number>(0)
  const [state, setState] = useTableState('applications-tagging-table', { start: 0, length: 25, order: { column: 0, dir: 'asc' }, search: '' })
  const selected = group > 0 ? group : (groups.data?.groups[0]?.ID ?? 0)
  const table = useQuery({ ...groupTableQuery(selected, request(state)), placeholderData: keepPreviousData })

  if (groups.error instanceof ApiError && groups.error.status === 403) return <Forbidden />
  if (!groups.data) return <Spinner />

  const selectedGroup = groups.data.groups.find((g) => g.ID === selected)
  const write = (input: HTMLInputElement) => send('/tagging/group', { application: Number(input.dataset.app), group: Number(input.dataset.group), tagging: Number(input.value) }, () => void client.invalidateQueries({ queryKey: ['settings', 'productivitee', 'tagging'] }))

  return (
    <div id="content">
      <header className="panel-heading" style={{ borderBottom: 0 }}>
        <h3><strong>Application</strong> tagging</h3>
        <label className="color">productivity applications tagging system</label>
      </header>
      <div className="row">
        <div className="col-md-3">
          <SettingsPicker id="groups-list">
            <SettingsPickerHeader label="Groups list" />
            {groups.data.groups.map((g) => (
              <SettingsPickerItem key={g.ID} label={g.name} active={g.ID === selected} onPick={() => { setGroup(g.ID); setState({ start: 0 }) }} liProps={{ className: 'category-tab', 'data-category': g.ID }} />
            ))}
          </SettingsPicker>
        </div>
        <div className="col-md-9" onClick={onTagClick(write)}>
          {selected === 0 ? (
            <div className="alert alert-info"><i className="fas fa-info-circle"></i> No groups in this company.</div>
          ) : (
            <>
              {selectedGroup && (
                <InactivityEditor
                  key={selectedGroup.ID}
                  group={selectedGroup}
                  onSaved={() => void client.invalidateQueries({ queryKey: ['settings', 'productivitee', 'groups'] })}
                />
              )}
              <DataTable
                id="applications-tagging-table"
                columns={groupColumns()}
                rows={(table.data?.data ?? []) as unknown as Record<string, unknown>[]}
                total={table.data?.recordsFiltered ?? table.data?.recordsTotal ?? 0}
                fetching={table.isFetching}
                error={table.error ? table.error.message : null}
                onRetry={() => void table.refetch()}
                state={state}
                onState={setState}
                rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.ID)}`)}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Timpul de inactivitate (idle) al grupului, editabil in MINUTE (implicit 5).
 *
 * Statiile din grup preiau valoarea grupului. Pe ecran se lucreaza in minute,
 * dar spre server pleaca in SECUNDE (contractul, si serverul ph4, sunt in
 * secunde): 1..60 min <=> 60..3600s. Ce iese din interval se opreste in UI, cu
 * un toast, INAINTE sa plece cererea. La reusita, lista de grupuri se reia
 * (`onSaved`), ca ecranul sa arate ce tine baza.
 *
 * Componenta se remonteaza la schimbarea grupului (`key={group.ID}` la parinte),
 * deci starea porneste mereu de la pragul grupului ales.
 */
const MIN_MINUTES = 1
const MAX_MINUTES = 60

function InactivityEditor({ group, onSaved }: { group: ProductiviteeGroup; onSaved: () => void }) {
  const [minutes, setMinutes] = useState(String(Math.max(MIN_MINUTES, Math.round(group.inactivitySeconds / 60))))
  const [saving, setSaving] = useState(false)

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const m = Number(minutes)
    if (!Number.isInteger(m) || m < MIN_MINUTES || m > MAX_MINUTES) {
      toast('Inactivity time must be between 1 and 60 minutes.', 'Error', 'error')
      return
    }
    setSaving(true)
    try {
      const body = await saveGroupInactivity(group.ID, m * 60)
      toast(body.message ?? 'Inactivity time was saved.', 'Success', 'success')
      onSaved()
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="form-inline inactivity_form" style={{ marginBottom: 14 }} onSubmit={onSubmit}>
      <label htmlFor="group-inactivity" style={{ marginRight: 10 }}>
        <strong>Inactivity time</strong>{' '}
        <span className="color">before a station in this group counts as idle</span>
      </label>
      <input
        id="group-inactivity"
        type="number"
        className="form-control inactivity_minutes"
        style={{ width: 90, marginRight: 6 }}
        min={MIN_MINUTES}
        max={MAX_MINUTES}
        step={1}
        value={minutes}
        aria-label="Inactivity time in minutes"
        onChange={(e) => setMinutes(e.target.value)}
      />
      <span style={{ marginRight: 10 }}>minutes</span>
      <button type="submit" className="btn btn-primary save_inactivity" disabled={saving}>Save</button>
    </form>
  )
}

/* ------------------------------------------------------ pe firma */

function MasterTagging() {
  const client = useQueryClient()
  const [state, setState] = useTableState('masterTagging', { start: 0, length: 25, order: { column: 0, dir: 'asc' }, search: '' })
  const table = useQuery({ ...companyTableQuery(request(state)), placeholderData: keepPreviousData })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const write = (input: HTMLInputElement) => send('/master/tag', { application: Number(input.dataset.app), tagging: Number(input.value) }, () => void client.invalidateQueries({ queryKey: ['settings', 'productivitee'] }))

  return (
    <div id="content">
      <header className="panel-heading" style={{ borderBottom: 0 }}>
        <h3><strong>Master</strong> tagging</h3>
        <label className="color">productivity applications tagging system for all groups</label>
      </header>
      {/*
        Tabelul sta intr-o COLOANA, ca pe Application tagging (`col-md-9`), nu
        direct in `.row` cu marginile stinse din stil. Cele doua cad azi in
        acelasi loc - coloana intoarce prin `padding` fix cei 15px pe care
        randul ii scoate prin `margin` - dar numai `margin: 0` tinea socoteala,
        iar invelisul lui DataTables isi scrie inauntru propriile randuri
        Bootstrap (cautarea, filtrele), care asteapta o coloana in jurul lor.
      */}
      <div className="row" onClick={onTagClick(write)}>
        <div className="col-md-12">
          <DataTable
            id="masterTagging"
            columns={companyColumns()}
            rows={(table.data?.data ?? []) as unknown as Record<string, unknown>[]}
            total={table.data?.recordsFiltered ?? table.data?.recordsTotal ?? 0}
            fetching={table.isFetching}
            error={table.error ? table.error.message : null}
            onRetry={() => void table.refetch()}
            state={state}
            onState={setState}
            rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.ID)}`)}
          />
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------- General settings (firma) */

/**
 * Settings > Productivitee > General settings.
 *
 * Doua formulare, fiecare cu butonul lui, si fiecare trimite NUMAI campurile
 * lui: raportul de seara catre oameni, si metoda implicita de calcul a firmei.
 *
 * SCRIEREA IA DATELE PE FATA (`save.mutate(payload)`), nu dintr-o inchidere
 * peste starea ecranului. O inchidere veche - una legata de starea din clipa in
 * care s-a desenat butonul - a sters odata randuri aici.
 *
 * CAND SERVERUL REFUZA, ecranul se aseaza inapoi pe ce tine serverul: raspunsul
 * unei scrieri reusite poarta setarile RECITITE din baza si ele se pun in
 * cache; o scriere refuzata spune motivul si cere din nou pagina. Asa nu ramane
 * pe ecran o valoare pe care n-o are nimeni.
 *
 * PROGRAMUL DE LUCRU AL UNUI OM NU SE SCRIE AICI. Exista deja, in Settings >
 * Users accounts > Work hour plans (`it_work_hour_plans` +
 * `it_work_hour_daily_schedule`, legate de cont prin `view_angajati.work_hour_plan`).
 * Ce sta aici e DREPTUL omului de a si-l schimba din pagina din email, nu inca
 * o copie a orelor.
 */
function GeneralSettings() {
  const client = useQueryClient()
  const page = useQuery(productiviteeGeneralQuery())
  /*
   * Semnul dupa care formularele se aseaza din nou pe ce spune serverul.
   *
   * Nu se poate sta pe identitatea datelor din cache: react-query imparte
   * structura, deci o recitire care da EXACT aceleasi valori intoarce acelasi
   * obiect, efectul nu se mai porneste, si ecranul ramane cu ce a scris omul
   * dupa o scriere refuzata - adica arata o valoare pe care n-o are nimeni.
   * Numarul asta creste la FIECARE scriere incheiata, reusita sau nu.
   */
  const [settled, setSettled] = useState(0)

  const save = useMutation({
    mutationFn: (write: ProductiviteeGeneralWrite) => saveProductiviteeGeneral(write),
    onSuccess: (answer) => {
      toast(answer.message ?? 'Productivitee settings saved.', 'Productivitee settings', 'success')
      /*
       * Ce a ramas SCRIS, nu ce s-a trimis - raspunsul poarta setarile recitite
       * din baza. Dar numai daca chiar le poarta: un raspuns scurt (un capat
       * schimbat, un intermediar care taie corpul) pus in cache ar lasa ecranul
       * fara `dayReport` si l-ar rupe la urmatoarea desenare. Cand nu le poarta,
       * se cere din nou pagina - o cerere in plus, niciodata un ecran alb.
       */
      if (hasSettings(answer)) client.setQueryData([...GENERAL_KEY], answer)
      else void client.invalidateQueries({ queryKey: [...GENERAL_KEY] })
    },
    onError: (err) => {
      toast(err instanceof ApiError && err.message ? err.message : 'Something went wrong.', 'Error', 'error')
      void client.invalidateQueries({ queryKey: [...GENERAL_KEY] })
    },
    onSettled: () => setSettled((n) => n + 1),
  })

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error) return <LoadFailed what="The Productivitee settings" error={page.error} onRetry={() => void page.refetch()} />
  if (!page.data) return <Spinner />

  return (
    <div id="content">
      <header className="panel-heading" style={{ borderBottom: 0 }}>
        <h3><strong>General</strong> settings</h3>
        <label className="color">productivity settings for the whole company</label>
      </header>
      {/*
        Formularele stau intr-o coloana, nu pe toata latimea: pe un ecran de
        1600px butonul `pull-right` ajungea la 1300px de campul pe care il
        salveaza, si nu se mai vedea al carui formular e. `col-md-9` e aceeasi
        latime pe care o are si tabelul de pe Application tagging.
      */}
      <div className="row">
        <div className="col-md-9">
          <DayReportForm page={page.data} save={save.mutate} saving={save.isPending} settled={settled} />
          <MethodForm page={page.data} save={save.mutate} saving={save.isPending} settled={settled} />
        </div>
      </div>
    </div>
  )
}

type SaveFn = (write: ProductiviteeGeneralWrite) => void

/** Raspunsul poarta chiar setarile, pe care ecranul le poate desena? */
function hasSettings(answer: unknown): answer is ProductiviteeGeneral {
  if (answer === null || typeof answer !== 'object') return false
  const page = answer as Partial<ProductiviteeGeneral>
  return typeof page.dayReport === 'object' && page.dayReport !== null && Array.isArray(page.weekdays) && Array.isArray(page.methods)
}

/** Capul unui sertar al ecranului, ca in fisa aparatului (`PanelHead`). */
function SectionHead({ strong, rest }: { strong: string; rest: string }) {
  return (
    <header className="panel-heading sm" data-color="theme-inverse">
      <h4><strong>{strong}</strong> {rest}</h4>
    </header>
  )
}

/**
 * Raportul de seara catre oameni.
 *
 * Zilele vin de la server cu numele lor (`weekdays`), din aceeasi tabela de pe
 * care cad si orele de program ale masinilor - deci ID-urile bifate aici sunt
 * aceleasi cu cele de acolo, si ecranul nu-si scrie a doua oara saptamana.
 */
function DayReportForm({ page, save, saving, settled }: { page: ProductiviteeGeneral; save: SaveFn; saving: boolean; settled: number }) {
  const report = page.dayReport
  const [enabled, setEnabled] = useState(report.enabled)
  const [hour, setHour] = useState(report.hour)
  const [days, setDays] = useState<number[]>(report.days)
  const [userHour, setUserHour] = useState(report.userMayChangeHour)
  const [userSchedule, setUserSchedule] = useState(report.userMayChangeSchedule)

  /* Ce spune serverul bate ce e pe ecran: dupa o scriere - reusita sau refuzata
     - formularul se aseaza pe ce tine serverul. Vezi `settled` in parinte. */
  useEffect(() => {
    setEnabled(report.enabled)
    setHour(report.hour)
    setDays(report.days)
    setUserHour(report.userMayChangeHour)
    setUserSchedule(report.userMayChangeSchedule)
  }, [report, settled])

  const toggleDay = (id: number, on: boolean) =>
    setDays((prev) => (on ? [...prev, id].sort((a, b) => a - b) : prev.filter((d) => d !== id)))

  return (
    <form
      className="form-horizontal day_report_form"
      onSubmit={(e) => {
        e.preventDefault()
        save({ enabled, hour, days, user_hour: userHour, user_schedule: userSchedule })
      }}
    >
      <SectionHead strong="Daily" rest="user report" />

      <div className="form-group">
        <label className="col-md-4 control-label">Send the daily report to users</label>
        <div className="col-md-8">
          <SettingsCheck
            name="enabled"
            aria-label="Send the daily report to users"
            checked={enabled}
            onChange={setEnabled}
            label="every measured person gets their own day by email"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="col-md-4 control-label" htmlFor="day-report-hour">Default hour</label>
        <div className="col-md-3">
          <input
            id="day-report-hour"
            type="time"
            className="form-control"
            name="hour"
            value={hour}
            onChange={(e) => setHour(e.target.value)}
          />
        </div>
        <div className="col-md-5">
          <span className="help-block" style={{ marginTop: 7 }}>
            when a person has not picked an hour of their own.
          </span>
        </div>
      </div>

      <div className="form-group">
        <label className="col-md-4 control-label">Days it goes out</label>
        <div className="col-md-8">
          {page.weekdays.map((day) => (
            <span key={day.id} style={{ marginRight: 14, display: 'inline-block' }}>
              <SettingsCheck
                name={'days[' + day.id + ']'}
                aria-label={day.name}
                checked={days.includes(day.id)}
                onChange={(on) => toggleDay(day.id, on)}
                label={day.name}
              />
            </span>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label className="col-md-4 control-label">What a person may change</label>
        <div className="col-md-8">
          <div>
            <SettingsCheck
              name="user_hour"
              aria-label="A person may change their own hour"
              checked={userHour}
              onChange={setUserHour}
              label="their own hour, from the page in the email"
            />
          </div>
          <div style={{ marginTop: 6 }}>
            <SettingsCheck
              name="user_schedule"
              aria-label="A person may change their own working hours"
              checked={userSchedule}
              onChange={setUserSchedule}
              label="their own working hours, from the page in the email"
            />
          </div>
          {/*
            Orele unui om nu se tin in Productivitee: ele sunt planurile din
            Settings > Users accounts. Randul asta e acolo ca nimeni sa nu le
            mai caute aici si sa faca al doilea loc pentru acelasi lucru.
          */}
          <span className="help-block">
            Working hours themselves live in{' '}
            <a href="/app/settings/users_accounts/work_hour_plans">Settings &rsaquo; Users accounts &rsaquo; Work hour plans</a>, per person.
          </span>
        </div>
      </div>

      <div className="form-group">
        <div className="col-md-12">
          <button type="submit" className="btn btn-primary pull-right save_day_report" disabled={saving}>Save</button>
        </div>
      </div>
    </form>
  )
}

/**
 * Metoda implicita de calcul a firmei.
 *
 * Pana acum se alegea numai pe MASINA (fisa aparatului > Settings >
 * Productivity method) - de doua sute de ori, o data pentru fiecare calculator.
 * Asta e cea de la care porneste o masina si cea folosita cand masina n-are una
 * a ei; masina care si-a ales alta ramane cu a ei, si nu i se schimba nimic de
 * aici.
 *
 * FELUL in care se socoteste cifra nu se atinge: functia stocata
 * `device_productivity` primeste mai departe metoda masinii.
 */
function MethodForm({ page, save, saving, settled }: { page: ProductiviteeGeneral; save: SaveFn; saving: boolean; settled: number }) {
  const [method, setMethod] = useState(page.method)

  useEffect(() => setMethod(page.method), [page.method, settled])

  return (
    <form
      className="form-horizontal method_form"
      style={{ marginTop: 20 }}
      onSubmit={(e) => {
        e.preventDefault()
        save({ method })
      }}
    >
      <SectionHead strong="Default" rest="productivity method" />

      <div className="form-group">
        <label className="col-md-4 control-label">Productivity method</label>
        <div className="col-md-8">
          {page.methods.map((m) => (
            <div key={m.ID} style={{ marginBottom: 4 }}>
              <SettingsRadio
                name="method"
                value={m.ID}
                checked={method === m.ID}
                onChange={(on) => { if (on) setMethod(m.ID) }}
                label={m.method_name}
              />
            </div>
          ))}
          <span className="help-block">
            What a device starts from, and what is used when a device has none of its own. A device keeps the right to differ.
          </span>
        </div>
      </div>

      <div className="form-group">
        <div className="col-md-12">
          <button type="submit" className="btn btn-primary pull-right save_method" disabled={saving}>Save</button>
        </div>
      </div>
    </form>
  )
}
